import {
  describeSentrySampling,
  IGNORED_BROWSER_ERRORS,
  IGNORED_ROOT_TRANSACTIONS,
  resolveSentrySampling,
} from '@alinea/core/observability';
import * as Sentry from '@sentry/react';

// Rates were hardcoded on `import.meta.env.PROD`, which is true for QA, preview and
// production alike — so all three shared one budget with no way to separate them. The policy
// now lives in one reviewed table keyed on VITE_SENTRY_ENVIRONMENT, which the deploy
// workflows already set to `qa`, `preview` and `prod`.
const sampling = resolveSentrySampling({
  service: 'web',
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
  env: import.meta.env as unknown as Record<string, string | undefined>,
});
console.log(describeSentrySampling('web', sampling));

// A missing DSN turns the whole SDK off silently: `enabled` below is false, nothing is
// sent, and the Sentry project simply stays empty. That is indistinguishable from "no
// errors happened", and it hid every browser crash in production until 2026-09-10. Say so
// out loud on any deployed environment; `dev` legitimately runs without a DSN.
if (!import.meta.env.VITE_SENTRY_DSN && sampling.environment !== 'dev') {
  console.warn(
    `[sentry] VITE_SENTRY_DSN is not set for environment "${sampling.environment}". ` +
      'Browser error reporting is DISABLED — the deploy workflow must pass it at build time.',
  );
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,

  // PII (IP addresses, request bodies, headers) leaves the browser ONLY in local development.
  // This shipped as an unconditional `true`.
  sendDefaultPii: sampling.environment === 'dev',

  // Performance monitoring — see the policy table in @alinea/core/observability.
  tracesSampleRate: sampling.traces,

  // Session replay, and the reason its two rates differ so sharply:
  //
  // Routine replay (`replaysSessionSampleRate`) recorded 10% of ALL production sessions of a
  // financial ERP — vendor names, invoice values, bank details, payroll — and did so with
  // `maskAllText: false`, i.e. in the clear. That is a standing privacy liability with no
  // matching benefit: nobody was watching those recordings, and they are billed per session.
  //
  // Error replay (`replaysOnErrorSampleRate`) is the shape worth paying for. The recording is
  // buffered in the browser and only uploaded when something actually broke, so we pay for a
  // session precisely when it has a question attached to it.
  replaysSessionSampleRate: sampling.replays,
  replaysOnErrorSampleRate: sampling.replaysOnError,

  enabled: !!import.meta.env.VITE_SENTRY_DSN,

  // Errors the application cannot act on: browser extensions, the benign ResizeObserver
  // warning, and the aborts a user closing a tab produces. Dropped before they are billed.
  ignoreErrors: [...IGNORED_BROWSER_ERRORS],
  // Platform probes and parentless command transactions — see IGNORED_ROOT_TRANSACTIONS.
  ignoreTransactions: [...IGNORED_ROOT_TRANSACTIONS],

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Mask by default and open up deliberately. Every text node and input is masked and
      // every image, video and canvas is blocked, so a recording cannot leak a value nobody
      // reviewed. To make a specific element legible in replays — a status badge, a step
      // name, an error banner — add `data-sentry-unmask` to it and nothing else changes.
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      unmask: ['[data-sentry-unmask]'],
    }),
  ],

  // Filter out sensitive data
  beforeSend(event) {
    // Remove sensitive data from breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
        if (breadcrumb.data?.headers) {
          delete breadcrumb.data.headers['authorization'];
        }
        return breadcrumb;
      });
    }
    return event;
  },
});

export { Sentry };
